import { Link, useNavigate } from "react-router-dom";
import React, { useState } from "react";
import { Card, Col, Form, Row } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import * as yup from "yup";
import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { AuthService } from "../login/service";
import { handleError } from "../../shared/functions/handleError";
import { enqueueSnackbar } from "notistack";

interface IForgotPassword {
    email: string;
}

export const forgotPasswordSchema = yup.object({
    email: yup.string().email().required("validation.required"),
});

const ForgotPassword = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [isSubmitted, setIsSubmitted] = useState(false);

    const {
        control,
        handleSubmit,
        formState: { errors, isSubmitting },
    } = useForm<IForgotPassword>({
        defaultValues: {
            email: "",
        },
        resolver: yupResolver(forgotPasswordSchema),
    });

    const onSubmit = async (data: IForgotPassword) => {
        await AuthService.forgotPassword(data.email)
            .then(() => {
                setIsSubmitted(true);
                enqueueSnackbar(t("auth.forgotPasswordSuccess"), {
                    variant: "success",
                });
            })
            .catch(handleError);
    };

    return (
        <React.Fragment>
            <div className="auth-main v1">
                <div className="auth-wrapper">
                    <div className="auth-form">
                        <Card className="my-5">
                            <Card.Body>
                                {isSubmitted ? (
                                    <div className="text-center">
                                        <div className="mb-4">
                                            <i className="ph-duotone ph-envelope-simple text-success" style={{ fontSize: '4rem' }}></i>
                                        </div>
                                        <h5 className="mb-3">{t("auth.checkYourEmail")}</h5>
                                        <p className="text-muted mb-4">
                                            {t("auth.forgotPasswordEmailSent")}
                                        </p>
                                        <Link to="/" className="btn btn-primary">
                                            {t("auth.backToLogin")}
                                        </Link>
                                    </div>
                                ) : (
                                    <>
                                        <div className="text-center">
                                            <img
                                                src={"/assets/images/authentication/img-auth-login.png"}
                                                alt="images"
                                                className="img-fluid mb-3"
                                            />
                                            <h4 className="f-w-500 mb-1">{t("auth.forgotPasswordTitle")}</h4>
                                            <p className="mb-3 text-muted">
                                                {t("auth.forgotPasswordDescription")}
                                            </p>
                                        </div>
                                        <Form onSubmit={handleSubmit(onSubmit)} noValidate>
                                            <Form.Group className="mb-3" controlId="email">
                                                <Form.Label>{t("general.email")}</Form.Label>
                                                <Controller
                                                    name="email"
                                                    control={control}
                                                    render={({ field }) => (
                                                        <Form.Control
                                                            {...field}
                                                            type="email"
                                                            placeholder={t("general.email")}
                                                            isInvalid={!!errors.email?.message}
                                                        />
                                                    )}
                                                />
                                                <Form.Control.Feedback type="invalid">
                                                    {errors.email?.message &&
                                                        t(errors.email.message as string)}
                                                </Form.Control.Feedback>
                                            </Form.Group>

                                            <div className="d-grid mt-4">
                                                <button
                                                    type="submit"
                                                    className="btn btn-primary"
                                                    disabled={isSubmitting}
                                                >
                                                    {isSubmitting ? t("auth.sending") : t("auth.sendResetLink")}
                                                </button>
                                            </div>

                                            <div className="text-center mt-3">
                                                <Link to="/">
                                                    <h6 className="f-w-400 mb-0">
                                                        {t("auth.backToLogin")}
                                                    </h6>
                                                </Link>
                                            </div>
                                        </Form>
                                    </>
                                )}
                            </Card.Body>
                        </Card>
                    </div>

                    <div className="auth-sidefooter">
                        <Row>
                            <Col md={2}>
                                <img
                                    src={"/assets/images/logo-horizontal.png"}
                                    className="img-brand img-fluid w-100"
                                    alt="images"
                                />
                            </Col>
                        </Row>

                        <hr className="mb-3 mt-4" />
                        <Row>
                            <div className="col my-1">
                                <p className="m-0">
                                    {t("general.developedBy")}
                                    <a href="https://itmedia.io/" target="_blank">
                                        {" "}
                                        itMedia
                                    </a>
                                </p>
                            </div>
                            <div className="col-auto my-1">
                                <ul className="list-inline footer-link mb-0">
                                    <li className="list-inline-item">
                                        <Link to="/">Home</Link>
                                    </li>
                                    <li className="list-inline-item">
                                        <Link to="https://itmedia.io/" target="_blank">
                                            Support
                                        </Link>
                                    </li>
                                </ul>
                            </div>
                        </Row>
                    </div>
                </div>
            </div>
        </React.Fragment>
    );
};

export default ForgotPassword;
